import { useState } from "react";
import { Button } from "react-bootstrap";
import sharedProperty from "../../../../app/sharedProperty";
import { showErrorMessage, showSuccessMessage } from "../../../../app/tools";
import { Icon, IconDefinitions, InfoSection, SlideOutModal } from "../../../../components";
import { GaugeConfig, PeakSignalModel } from "../../../../data/entities";
import { buildSignalDetails } from "./peakSignalDetails";

interface DeletePeakSignalModalProps {
  showState: sharedProperty<boolean>;
  signal: PeakSignalModel | null;
  config: GaugeConfig | null;
  onConfirm: (signal: PeakSignalModel) => void | Promise<void>;
}

export function DeletePeakSignalModal({ showState, signal, config, onConfirm }: DeletePeakSignalModalProps) {
  const [deleting, setDeleting] = useState(false);

  if (!signal) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm(signal);
      showSuccessMessage("Peak signal deleted");
      showState.setter(false);
    } catch (error) {
      console.error("Error deleting peak signal:", error);
      showErrorMessage("Failed to delete peak signal");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SlideOutModal
      showState={showState}
      title="Delete Peak Signal"
      footer={
        <div className="row m-0 w-100">
          <div className="col-6 ps-0">
            <Button
              onClick={() => showState.setter(false)}
              variant="cancel"
              className="w-100"
              disabled={deleting}
            >
              Cancel
            </Button>
          </div>
          <div className="col-6 pe-0">
            <Button
              onClick={handleDelete}
              variant="danger"
              className="w-100"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Deleting...
                </>
              ) : (
                "Delete Peak Signal"
              )}
            </Button>
          </div>
        </div>
      }
    >
      <InfoSection title="Peak Signal" details={buildSignalDetails(signal, config)} />

      <div className="alert alert-warning py-2 mb-0">
        <small className="d-flex">
          <span className="me-2 flex-shrink-0">
            <Icon iconName={IconDefinitions.triangleExclamation} />
          </span>
          <span>
            This permanently removes the peak signal from the log. The inspection's
            peak gauges and status will be recalculated.
          </span>
        </small>
      </div>
    </SlideOutModal>
  );
}
